import { Box, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
    CUSTOM_DISCLAIMER_ENABLED_KEY,
    CUSTOM_DISCLAIMER_MESSAGE_KEY,
    MarketplaceStatus,
    saveCustomDisclaimerThunk
} from '@spike/marketplace-action';
import { SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';
import { reduceResolution, wbp } from 'Theme';
import clsx from 'clsx';
import { Switch } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

export interface Props {
    disabled?: boolean;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            borderTop: '1px solid #E5E5E5',
            [theme.breakpoints.down(wbp)]: {
                marginTop: `${reduceResolution(47)}px`,
                paddingTop: `${reduceResolution(47)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginTop: '47px',
                paddingTop: '47px'
            }
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        title: {
            color: '#222222',
            fontWeight: 600,
            fontSize: '16px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '16px'
            }
        },
        description: {
            color: '#222222',
            marginTop: '8px',
            fontSize: '16px',
            lineHeight: '25px',
            [theme.breakpoints.down('sm')]: {
                marginTop: `${reduceResolution(20)}px`,
                fontSize: '14px',
                lineHeight: '140%'
            }
        },
        disclaimerBox: {
            'marginTop': '24px',
            'display': 'flex',
            'flexDirection': 'column',
            'border': '1px solid #D3D3D3',
            'borderRadius': '9px',
            'backgroundColor': '#FFFFFF',
            'overflow': 'hidden',
            '&:focus-within': {
                borderColor: '#222222'
            }
        },
        textarea: {
            'width': '100%',
            'minHeight': '110px',
            'boxSizing': 'border-box',
            'border': 'none',
            'outline': 'none',
            'resize': 'none',
            'padding': '16px 20px',
            'fontFamily': 'inherit',
            'fontSize': '16px',
            'lineHeight': '25px',
            'color': '#222222',
            '&::placeholder': {
                color: '#7A7A7A',
                opacity: 1
            },
            '&:disabled': {
                backgroundColor: '#F4F4F4'
            }
        },
        footer: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '10px 14px'
        }
    })
);

export const CustomDisclaimer: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const settings = useSelector((state: RootState) => state.marketplace.marketplace.settings);
    const savedEnabled =
        settings.find(setting => setting.key === CUSTOM_DISCLAIMER_ENABLED_KEY)?.value === SETTINGS_TRUE_VALUE;
    const savedMessage = settings.find(setting => setting.key === CUSTOM_DISCLAIMER_MESSAGE_KEY)?.value ?? '';
    const status = useSelector((state: RootState) => state.marketplace.status);

    const [enabled, setEnabled] = useState(savedEnabled);
    const [message, setMessage] = useState(savedMessage);

    const isSaving = status === MarketplaceStatus.SavingCustomDisclaimer;

    const toggleHandler = (checked: boolean) => {
        setEnabled(checked);
        dispatch(saveCustomDisclaimerThunk(apiClientWrapper, checked, message));
    };

    const saveHandler = () => {
        dispatch(saveCustomDisclaimerThunk(apiClientWrapper, true, message));
    };

    return (
        <Box className={clsx(classes.container, props.className)}>
            <Box className={classes.header}>
                <Typography className={classes.title}>Custom Disclaimer for Online Booking</Typography>
                <Switch
                    checked={enabled}
                    disabled={props.disabled}
                    onChange={toggleHandler}
                />
            </Box>
            <Typography className={classes.description}>
                Add a message to inform clients of any key policies or instructions during the online booking process.
            </Typography>
            {enabled && (
                <Box className={classes.disclaimerBox}>
                    <textarea
                        className={classes.textarea}
                        placeholder="Write your disclaimer here..."
                        value={message}
                        disabled={props.disabled}
                        onChange={event => setMessage(event.target.value)}
                    />
                    <Box className={classes.footer}>
                        <Button
                            label={isSaving ? 'Saving...' : 'Save'}
                            variant="green"
                            isLoading={isSaving}
                            disabled={props.disabled || isSaving || message === savedMessage}
                            onClick={saveHandler}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default CustomDisclaimer;
